// =============================================================================
// CONTENT RENDERER COMPONENT
// =============================================================================

import React from 'react';
import type { ContentComponent } from '@/types';
import {
  HeroSection,
  TextSection,
  CardSection,
  FeaturesSection,
  StatsSection,
  CTASection,
  TestimonialSection,
  GallerySection,
  ImageShowcaseSection,
  StoryGallerySection,
  TimelineSection,
  PricingSection,
  FAQSection,
  TeamSection,
  LogoCloudSection,
  NewsletterSection,
} from '@/components/ContentComponents';

interface ContentRendererProps {
  components: ContentComponent[];
}

export const ContentRenderer: React.FC<ContentRendererProps> = ({ components }) => {
  const renderComponent = (component: ContentComponent) => {
    const ComponentWrapper = ({ children }: { children: React.ReactNode }) => {
      const wrapperClasses = component.fullWidth ? 'full-width' : '';
      return (
        <div className={wrapperClasses}>
          {children}
        </div>
      );
    };

    let componentElement: React.ReactNode;
    
    switch (component.type) {
      case 'hero':
        componentElement = <HeroSection key={component.id} component={component} />;
        break;
      case 'text':
        componentElement = <TextSection key={component.id} component={component} />;
        break;
      case 'card':
        componentElement = <CardSection key={component.id} component={component} />;
        break;
      case 'features':
        componentElement = <FeaturesSection key={component.id} component={component} />;
        break;
      case 'stats':
        componentElement = <StatsSection key={component.id} component={component} />;
        break;
      case 'cta':
        componentElement = <CTASection key={component.id} component={component} />;
        break;
      case 'testimonial':
        componentElement = <TestimonialSection key={component.id} component={component} />;
        break;
      case 'gallery':
        componentElement = <GallerySection key={component.id} component={component} />;
        break;
      case 'timeline':
        componentElement = <TimelineSection key={component.id} component={component} />;
        break;
      case 'pricing':
        componentElement = <PricingSection key={component.id} component={component} />;
        break;
      case 'faq':
        componentElement = <FAQSection key={component.id} component={component} />;
        break;
      case 'team':
        componentElement = <TeamSection key={component.id} component={component} />;
        break;
      case 'logo-cloud':
        componentElement = <LogoCloudSection key={component.id} component={component} />;
        break;
      case 'newsletter':
        componentElement = <NewsletterSection key={component.id} component={component} />;
        break;
      case 'story-gallery':
        componentElement = <StoryGallerySection key={component.id} component={component} />;
        break;
      case 'fullwidth-hero':
        // Deprecated: Use type 'hero' with fullWidth property instead
        componentElement = <HeroSection key={component.id} component={{ ...component, fullWidth: true }} />;
        break;
      case 'image-showcase':
        componentElement = <ImageShowcaseSection key={component.id} component={component} />;
        break;
      default:
        console.warn(`Unknown component type: ${component.type}`);
        return null;
    }

    return (
      <ComponentWrapper key={component.id}>
        {componentElement}
      </ComponentWrapper>
    );
  };

  return (
    <div className="content-renderer">
      {components.map(renderComponent)}
    </div>
  );
};
